"use client";

import { awsS3BaseUrl } from "@/lib/aws/s3";
import { Lng } from "@/lib/i18n/settings";
import { SectionKey } from "@/lib/schema/sections";
import { TResume } from "@/lib/store/resume";
import { Azurill } from "./Azurill";
import { Bronzor } from "./Bronzor";
import { Chikorita } from "./Chikorita";
import { CurveElite } from "./CurveElite";
import { CurveEliteSimplified } from "./CurveEliteSimplified";
import { Essential } from "./Essential";
import { EssentialPro } from "./EssentialPro";
import { Glalie } from "./Glalie";
import { Gradient } from "./Gradient";
import { JIS } from "./JIS";
import { JIS002 } from "./JIS002";
import { Leafish } from "./Leafish";
import { NoPicture001 } from "./NoPicture001";
import { NoPicture002 } from "./NoPicture002";
import { Pikachu } from "./Pikachu";
import { Reed } from "./Reed";
import { Rhyhorn } from "./Rhyhorn";
import { RhyhornPrimary } from "./RhyhornPrimary";
import { Rirekisho } from "./Rirekisho";
import { Simple001 } from "./Simple001";
import { Simple002 } from "./Simple002";
import { Simple003 } from "./Simple003";
import { Vegan } from "./Vegan";

export type TemplateProps = {
  resume: TResume;
  columns: SectionKey[][];
  isFirstPage?: boolean;
};

export const getTemplateList = (language: Lng) => {
  let lng = language;
  const imgLngList = [Lng.zh, Lng.en, Lng.ja];
  if (!imgLngList.includes(lng)) {
    lng = Lng.en;
  }
  return [
    {
      id: "jis",
      languages: [Lng.ja],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/jis.webp`,
      isNew: false,
    },
    {
      id: "jis002",
      languages: [Lng.ja],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/jis002.webp`,
      isNew: false,
    },
    {
      id: "rirekisho",
      languages: [Lng.ja],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/rirekisho.webp`,
      isNew: false,
    },
    {
      id: "simple001",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/simple001.webp`,
      isNew: false,
    },
    {
      id: "simple002",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/simple002.webp`,
      isNew: false,
    },
    {
      id: "simple003",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/simple003.webp`,
      isNew: false,
    },
    {
      id: "gradient",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/gradient.webp`,
      isNew: true,
    },
    {
      id: "vegan",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/vegan.webp`,
      isNew: true,
    },
    {
      id: "rhyhorn",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/rhyhorn.webp`,
      isNew: false,
    },
    {
      id: "rhyhornPrimary",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/rhyhornPrimary.webp`,
      isNew: false,
    },
    {
      id: "essential",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/essential.webp`,
      isNew: true,
    },
    {
      id: "essentialPro",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/essentialPro.webp`,
      isNew: true,
    },
    {
      id: "curveEliteSimplified",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/curveEliteSimplified.webp`,
      isNew: true,
    },
    {
      id: "curveElite",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/curveElite.webp`,
      isNew: true,
    },
    {
      id: "reed",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/reed.webp`,
      isNew: true,
    },
    {
      id: "noPicture001",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/noPicture001.webp`,
      isNew: false,
    },
    {
      id: "noPicture002",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/noPicture002.webp`,
      isNew: false,
    },
    {
      id: "leafish",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/leafish.webp`,
      isNew: false,
    },
    {
      id: "azurill",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/azurill.webp`,
      isNew: false,
    },
    {
      id: "bronzor",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/bronzor.webp`,
      isNew: false,
    },
    {
      id: "chikorita",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/chikorita.webp`,
      isNew: false,
    },
    {
      id: "glalie",
      languages: [Lng.zh, Lng.en, Lng.ja, Lng.hk],
      previewImg: `${awsS3BaseUrl}app/resumes/builtin/${lng}/glalie.webp`,
      isNew: false,
    },
  ].filter((res) => {
    if (res.languages.includes(lng)) {
      return true;
    }
    return false;
  });
};

export const defaultTemplate = "rhyhornPrimary";

export const getTemplate = (template: string) => {
  switch (template) {
    case "azurill":
      return Azurill;
    case "rhyhornPrimary":
      return RhyhornPrimary;
    case "bronzor":
      return Bronzor;
    case "chikorita":
      return Chikorita;
    case "rhyhorn":
      return Rhyhorn;
    case "leafish":
      return Leafish;
    case "glalie":
      return Glalie;
    case "pikachu":
      return Pikachu;
    case "noPicture001":
      return NoPicture001;
    case "noPicture002":
      return NoPicture002;
    case "rirekisho":
      return Rirekisho;
    case "simple001":
      return Simple001;
    case "simple002":
      return Simple002;
    case "simple003":
      return Simple003;
    case "vegan":
      return Vegan;
    case "jis":
      return JIS;
    case "jis002":
      return JIS002;
    case "gradient":
      return Gradient;
    case "essential":
      return Essential;
    case "essentialPro":
      return EssentialPro;
    case "curveElite":
      return CurveElite;
    case "curveEliteSimplified":
      return CurveEliteSimplified;
    case "reed":
      return Reed;
    default:
      return RhyhornPrimary;
  }
};
